import { Controller } from "@hotwired/stimulus"

// Drag source for the employee layout's inverted panel: the cards are Baustellen and Aufgaben,
// dragged ONTO an employee row. One delegated dragstart on the panel (never one controller per
// card) emits the same {resourceType, resourceId, resourceName} payload the employee/fleet cards
// use, so planning-employee-board's drop handler needs no special case.
export default class extends Controller {
  static targets = [
    "search",
    "noMatch",
    "body",
    "chevron",
    "modeChip",
    "count",
    "durationChip",
    "limitHint",
  ]
  static values = { expanded: Boolean, searchUrl: String }

  // Where the hourly board's default drop length lives — the board controller reads the same
  // key when previewing and creating a drop, so the chips are the single visible control.
  static DURATION_STORAGE_KEY = "planning_hourly_drop_minutes"

  // The panel renders only the first Site::BOARD_DISPLAY_LIMIT Baustellen, so a term typed here
  // has to reach the server to find anything past that cap. Long enough that typing a word does
  // not fire a request per keystroke.
  static SEARCH_DEBOUNCE_MS = 300

  connect() {
    this.mode = "all"
    this.applyExpanded()
    this.paintDurations()
    this.boundFrameLoad = this.frameLoaded.bind(this)
    this.element.addEventListener("turbo:frame-load", this.boundFrameLoad)
    // The panel is docked to the viewport, so nothing in the layout constrains it to the board's
    // width. Track the board's box instead: the left nav and the stats sidebar both collapse, and
    // no CSS inset can follow either.
    this.syncGeometry = this.syncGeometry.bind(this)
    this.board = document.querySelector("#planning_board")
    if (this.board) {
      this.observer = new ResizeObserver(this.syncGeometry)
      this.observer.observe(this.board)
      this.observer.observe(document.documentElement)
    }
    window.addEventListener("resize", this.syncGeometry)
    this.syncGeometry()
  }

  disconnect() {
    this.observer?.disconnect()
    window.removeEventListener("resize", this.syncGeometry)
    this.element.removeEventListener("turbo:frame-load", this.boundFrameLoad)
    clearTimeout(this.searchTimeout)
  }

  syncGeometry() {
    if (!this.board) return

    const rect = this.board.getBoundingClientRect()
    this.element.style.left = `${Math.round(rect.left)}px`
    this.element.style.width = `${Math.round(rect.width)}px`
    this.element.style.right = "auto"
    this.syncSpacer()
  }

  // Keep a spacer as tall as the panel so the last employee row can always be scrolled clear of it.
  syncSpacer() {
    const spacer = document.querySelector("#planning_bottom_spacer")
    if (spacer) spacer.style.height = `${Math.round(this.element.getBoundingClientRect().height)}px`
  }

  // Collapsed to its header strip by default, like the shared resource panel — the panel must
  // never cover the rows it plans onto.
  toggle() {
    this.expandedValue = !this.expandedValue
    this.applyExpanded()
    if (this.expandedValue) this.searchTarget.focus()
  }

  applyExpanded() {
    // The body is a flex column inside a height-capped card, so it must become `flex` (not the
    // default block) when shown or the inner list loses its flex-fill and the cap does nothing.
    this.bodyTarget.classList.toggle("hidden", !this.expandedValue)
    this.bodyTarget.classList.toggle("flex", this.expandedValue)
    this.chevronTarget.classList.toggle("rotate-180", this.expandedValue)
    this.syncSpacer()
  }

  dragstart(event) {
    const card = event.target.closest("[data-resource-type]")
    if (!card) return

    event.dataTransfer.effectAllowed = "copy"
    event.dataTransfer.setData(
      "application/json",
      JSON.stringify({
        resourceType: card.dataset.resourceType,
        resourceId: Number(card.dataset.resourceId),
        resourceName: card.dataset.resourceName,
      }),
    )
    // The hourly board's drop preview needs to know WHAT is dragged while it is still in the
    // air, and dragover events cannot read the dataTransfer payload (browser protected mode).
    // Stash the identity on the document; dragend clears it.
    document.documentElement.dataset.planningDragPlannable = `${card.dataset.resourceType}-${card.dataset.resourceId}`
  }

  dragend() {
    delete document.documentElement.dataset.planningDragPlannable
  }

  // Sites-only vs sites-and-tasks: a coarse-planning switch, because the Aufgaben chips can
  // outnumber the Baustellen many times over. Composes with the search below.
  pickDuration(event) {
    try {
      localStorage.setItem(
        this.constructor.DURATION_STORAGE_KEY,
        event.currentTarget.dataset.dropDuration,
      )
    } catch {
      // Storage can be unavailable (private mode) — the chips still paint, drops fall back to 30.
    }
    this.paintDurations()
  }

  paintDurations() {
    if (!this.hasDurationChipTarget) return

    let stored
    try {
      stored = localStorage.getItem(this.constructor.DURATION_STORAGE_KEY)
    } catch {
      stored = null
    }
    const active = stored || "30"
    this.durationChipTargets.forEach((chip) => {
      const on = chip.dataset.dropDuration === active
      chip.classList.toggle("bg-gray-200", on)
      chip.classList.toggle("bg-white", !on)
      chip.classList.toggle("font-medium", on)
    })
  }

  setMode(event) {
    this.mode = event.currentTarget.dataset.mode || "all"
    this.modeChipTargets.forEach((chip) => {
      const on = (chip.dataset.mode || "all") === this.mode
      chip.classList.toggle("bg-gray-200", on)
      chip.classList.toggle("bg-white", !on)
      chip.classList.toggle("font-medium", on)
    })
    this.paintCount()
    this.search()
  }

  paintCount() {
    if (!this.hasCountTarget) return

    this.countTarget.textContent =
      this.mode === "sites"
        ? this.countTarget.dataset.countSites
        : this.countTarget.dataset.countAll
  }

  // Typing filters the rendered cards instantly (below) AND, debounced, asks the server for
  // matches across every Baustelle — the rendered list is only the first hundred, so the local
  // pass alone can never find what sits past the cap.
  search() {
    this.filterRendered()
    clearTimeout(this.searchTimeout)
    this.searchTimeout = setTimeout(() => this.fetchCards(), this.constructor.SEARCH_DEBOUNCE_MS)
  }

  fetchCards() {
    const frame = this.cardsFrame()
    if (!frame || !this.hasSearchUrlValue) return

    const url = new URL(this.searchUrlValue, window.location.origin)
    const term = this.searchTarget.value.trim()
    if (term) url.searchParams.set("query", term)
    url.searchParams.set("mode", this.mode)

    // Turbo ignores an assignment identical to the current src, so an unchanged query (e.g. the
    // mode chips re-firing the search) needs the explicit reload.
    if (frame.getAttribute("src") === url.pathname + url.search) {
      frame.reload()
    } else {
      frame.src = url.pathname + url.search
    }
  }

  cardsFrame() {
    return this.element.querySelector("#planning_plannable_cards")
  }

  // A fresh card set arrived: the header counts and the "erste N von M" hint live OUTSIDE the
  // frame (they sit in the collapsed strip), so they are rewritten from the frame's attributes.
  frameLoaded(event) {
    // Read from the inner meta element, never the frame: Turbo replaces a frame's CHILDREN and
    // leaves the frame element's own attributes at their first-render values.
    const meta = event.target.querySelector("[data-cards-meta]")
    if (!meta) return

    if (this.hasCountTarget) {
      this.countTarget.dataset.countAll = meta.dataset.cardCount || "0"
      this.countTarget.dataset.countSites = meta.dataset.siteCount || "0"
    }
    this.paintCount()

    if (this.hasLimitHintTarget) {
      const hint = meta.dataset.limitHint || ""
      this.limitHintTarget.textContent = hint
      this.limitHintTarget.classList.toggle("sm:inline", hint !== "")
    }

    // The term is NOT re-applied here: the server already matched it, and it matches fields the
    // cards do not carry (a Baustellen-Nummer is not part of a card's search text), so re-filtering
    // locally would hide the very hits that were just fetched. Only the sites-only mode applies.
    this.filterRendered({ applyTerm: false })
    this.syncSpacer()
  }

  // Client-side filter over the rendered cards. Aufgaben are routinely named the same on every
  // Baustelle ("Abschnitt 1"), so a group whose tasks match keeps its Baustelle card visible as the
  // group's label — otherwise the result is rows of identical chips with no clue where they belong.
  filterRendered({ applyTerm = true } = {}) {
    const term = applyTerm ? this.searchTarget.value.trim().toLowerCase() : ""
    const sitesOnly = this.mode === "sites"
    let matches = 0

    this.element.querySelectorAll("[data-plannable-group]").forEach((group) => {
      const cards = [...group.querySelectorAll("[data-resource-type]")]
      const anchor = group.querySelector("[data-plannable-anchor]")
      let groupMatches = 0

      cards.forEach((card) => {
        const allowed = !sitesOnly || card.hasAttribute("data-plannable-anchor")
        const hit = allowed && (!term || (card.dataset.searchText || "").includes(term))
        card.classList.toggle("hidden", !hit)
        if (hit) groupMatches += 1
      })

      // Show the Baustelle even when only its Aufgaben matched, and mute it so it still reads as
      // context rather than as a hit of its own.
      const anchorMatched = !term || (anchor?.dataset.searchText || "").includes(term)
      if (anchor && groupMatches > 0 && !anchorMatched) anchor.classList.remove("hidden")
      anchor?.classList.toggle("opacity-60", Boolean(term) && !anchorMatched)

      group.classList.toggle("hidden", groupMatches === 0)
      matches += groupMatches
    })

    if (this.hasNoMatchTarget) this.noMatchTarget.classList.toggle("hidden", matches > 0)
  }
}
